<template>
  <div class="rm-screen">
    <div v-if="loading" class="rm-empty">
      <v-progress-circular indeterminate color="#c2410c"></v-progress-circular>
    </div>
    <div v-else-if="!cards.length" class="rm-empty">
      <template v-if="search">{{ $t("report_mobile.no_results_for", { search }) }}</template>
      <template v-else>
        <v-icon size="40" color="#8a817a">mdi-food-off</v-icon>
        {{ $t("no_data_available") }}
      </template>
    </div>

    <template v-else>
      <div class="rm-stats rm-stats--pair">
        <div class="rm-stat rm-stat--inverse">
          <span class="rm-stat__label">{{ $t("report_desktop.total_meals") }}</span>
          <span class="rm-stat__value rm-num">{{ mealTotal }}</span>
        </div>
        <div class="rm-stat">
          <span class="rm-stat__label">{{ $t("report_desktop.workers") }}</span>
          <span class="rm-stat__value rm-num">{{ workerTotal }}</span>
        </div>
      </div>

      <section v-for="card in cards" :key="card.key" class="rm-card rm-mcard">
        <button
          type="button"
          class="rm-mcard__head"
          :aria-expanded="String(isOpen(card.key))"
          @click="toggle(card.key)"
        >
          <span class="rm-mcard__title">
            <span class="rm-mcard__name">{{ card.name }}</span>
            <span class="rm-muted">{{ pluralT("report_mobile.meals_n", card.total) }}</span>
          </span>
          <v-icon size="18" color="#8a817a" class="rm-mcard__chevron" :class="{ 'is-open': isOpen(card.key) }">
            mdi-chevron-down
          </v-icon>
        </button>

        <ul v-if="isOpen(card.key)" class="rm-list rm-mcard__list">
          <li v-for="meal in card.meals" :key="meal.key" class="rm-mrow">
            <div class="rm-mrow__main">
              <span class="rm-mrow__name">{{ meal.name }}</span>
              <span class="rm-mrow__value rm-num">{{ meal.total }}</span>
            </div>
            <div v-for="shift in meal.shifts" :key="shift.id" class="cm-me-shift">
              <report-shift-tag :shift="shift.id"></report-shift-tag>
              <span class="cm-me-workers">
                <span v-for="worker in shift.workers" :key="worker.id" class="cm-me-worker"><b v-if="worker.quantity > 1" class="rm-num">{{ worker.quantity }}× </b>{{ worker.name }}</span>
              </span>
            </div>
          </li>
        </ul>
      </section>
    </template>
  </div>
</template>

<script>
import CompanyReportTabMealEmployees from "@/components/company/reports/companyTabs/companyReportTabMealEmployees.vue";
import { SHIFTS } from "@/constants/shifts";
import hasPlural from "@/components/report/shared/hasPlural";
import ReportShiftTag from "@/components/report/shared/ReportShiftTag.vue";
import "@/components/report/mobile/screens/reportMobileMealCards.scss";
import { hasPeriodRefetch } from "../../hasPeriodRefetch";

/**
 * Mobilni "Obrok radnici": po firmi obroci, a ispod svakog radnici po smenama (ko je šta naručio).
 * Nasleđuje učitavanje, štampu i Excel iz starog taba.
 */
export default {
  name: "CompanyMobileMealEmployees",
  extends: CompanyReportTabMealEmployees,
  mixins: [hasPlural, hasPeriodRefetch("fetchUserlist")],
  components: { ReportShiftTag },
  data: () => ({
    closed: {},
  }),
  computed: {
    /** Firme sa obrocima; radnici po smeni, grupisani (isti radnik sa više porcija = "2× Ime Prezime"). */
    cards() {
      if (!this.report) {
        return [];
      }
      const search = this.search;
      return Object.entries(this.report)
        .map(([companyName, data], index) => {
          const meals = (data.meals || [])
            .filter((meal) => meal.total > 0 && meal.id)
            .map((meal) => {
              const shifts = SHIFTS.map((shift) => ({
                id: shift.id,
                workers: this.groupWorkers((meal.users || {})[`${shift.id}-smena`]),
              })).filter((shift) => shift.workers.length);
              const name = this.toLatin(meal.name);
              return {
                key: `${index}-${meal.id}`,
                name,
                total: meal.total,
                shifts,
                searchText: [name, ...shifts.flatMap((shift) => shift.workers.map((worker) => worker.name))].join(" "),
              };
            });
          const found = search ? this.customSearch(meals, [{ value: "searchText" }], search) : meals;
          return {
            key: `${index}-${companyName}`,
            name: this.toLatin(companyName),
            total: found.reduce((sum, meal) => sum + meal.total, 0),
            meals: found,
          };
        })
        .filter((card) => card.meals.length);
    },
    mealTotal() {
      return this.cards.reduce((sum, card) => sum + card.total, 0);
    },
    workerTotal() {
      const ids = new Set();
      this.cards.forEach((card) =>
        card.meals.forEach((meal) => meal.shifts.forEach((shift) => shift.workers.forEach((worker) => ids.add(worker.id))))
      );
      return ids.size;
    },
  },
  methods: {
    groupWorkers(users) {
      const grouped = new Map();
      (users || []).forEach((user) => {
        const found = grouped.get(user.id);
        if (found) {
          found.quantity += user.quantity;
        } else {
          grouped.set(user.id, {
            id: user.id,
            name: [this.toLatin(user.name), this.toLatin(user.lastname)].filter(Boolean).join(" "),
            quantity: user.quantity,
          });
        }
      });
      return [...grouped.values()].sort((a, b) => a.name.localeCompare(b.name));
    },
    isOpen(key) {
      return Boolean(this.search) || !this.closed[key];
    },
    toggle(key) {
      this.closed = { ...this.closed, [key]: !this.closed[key] };
    },
  },
};
</script>

<style lang="scss">
.cm-me-shift {
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-top: 8px;
  font-size: 14px;
  line-height: 1.45;

  .rs-tag {
    margin-top: 1px;
  }
}

.cm-me-workers {
  flex: 1;
  min-width: 0;
  overflow-wrap: anywhere;
}

.cm-me-worker {
  &:not(:last-child)::after {
    content: ", ";
  }

  b {
    font-weight: 700;
  }
}
</style>
